import { CalendarDate, ZonedDateTime } from '@internationalized/date';
import {
  BookingStatus,
  CurrencyCode,
  DeductionApplicationType,
  LoadLineItem,
  LoadReconciliation,
  LoadStatus,
  Money,
  PaymentTerm,
  SalesItemLineType,
  SalesLoadLineItemPhase,
  SalesOrderStatus,
  Unit,
  Weight,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';

import { FileMetadata } from '~/shared/types/file';

export type WeightUnitType = 'scale' | 'price';

export type LoadDetailsTab =
  | 'load-info'
  | 'materials'
  | 'photos'
  | 'documents'
  | 'notes';

export enum enumLoadWeightType {
  Gross = 'gross',
  Tare = 'tare',
  Net = 'net',
  QuantityOwed = 'quantityOwed',
}

export type LineItemPhotoStatus =
  | 'none'
  | 'captured'
  | 'uploading'
  | 'uploaded'
  | 'error';

export type LineItemPhoto = {
  id?: string;
  previewUrl?: string;
  downloadUrl?: string;
  status: LineItemPhotoStatus;
  error?: string;
  capturedAt?: string;
};

export type LoadReconciliationDeduction = {
  id: string;
  reconciliationId: string;
  loadLineItemId: string;
  deductionTypeId: string;
  quantity: number;
  unitPrice: number;
  totalDeduction: number;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  };
};

export interface LoadDeductionRecord {
  id: string;
  loadId: string;
  deductionTypeId: string;
  deductionType?: {
    id: string;
    name: string;
    applicationType: DeductionApplicationType;
  } | null;
  applicationType: DeductionApplicationType;
  weightValue?: Weight | null;
  priceValue?: Money | null;
  percentage?: number | null;
  calculatedAmount?: Money | null;
  notes?: string | null;
  loadLineItemId?: string | null;
}

export interface LoadLineItemRecord {
  id: string;
  loadId?: string | null;
  type: SalesItemLineType;
  itemId: string | null;
  item?: {
    id: string;
    name: string;
    skuNumber?: string | null;
  } | null;
  tagId?: string | null;
  phase?: SalesLoadLineItemPhase | null;
  unitPrice?: Money | null;
  estimatedPrice?: Money | null;
  pricingUnit?: string | null;
  weights?: Record<string, Weight> | null;
  notes?: string | null;
  catalyticConverterSize?: string;
  /* @deprecated Use itemId matching with itemId instead */
  salesOrderLineId?: string | null;
}

export interface LoadReconciliationRecord {
  id: string;
  loadId: string;
  ticketNumber?: string | null;
  loadReceivedDate?: string | null;
  ticketIssueDate?: string | null;
  millReceivedWeightPostDate?: string | null;
  externalNotes?: string | null;
  internalNotes?: string | null;
  invoiceId?: string | null;
  invoiceType?: string | null;
  files: Array<{
    id: string;
    fileName: string;
    fileSize?: number | null;
    mimeType: string;
    status: string;
    downloadUrl?: string;
    metadata?: FileMetadata;
    timestamps: {
      createdAt: string;
      updatedAt: string;
    };
  }>;
  receivedLineItems: Array<{
    id: string;
    loadLineItemId: string;
    materialType: string;
    grossWeight: Weight | null;
    tareWeight: Weight | null;
    netWeight: Weight;
    customerGrossWeight?: Weight | null;
    customerTareWeight?: Weight | null;
    unitPrice: Money | null;
    customerUnitPrice?: Money | null;
    pricingUnit: Unit;
    totalValue: Money;
    itemId?: string | null;
    notes?: string | null;
    timestamps: {
      createdAt: string;
      updatedAt: string;
    };
  }>;
  deductions: Array<{
    id: string;
    receivedLineItemId?: string | null;
    deductionTypeId: string;
    weightQuantity?: Weight | null;
    priceQuantity?: Money | null;
  }>;
  freightCharges: Array<{
    id: string;
    hauler: string;
    freightLane: string;
    deliveryNumber: string;
    accruedAmount: Money;
    chargebackAmount: Money;
    notes?: string | null;
  }>;
}

export type LoadsTabListItem = {
  id: string;
  loadNumber: string;
  status: LoadStatus;
  reviewed: boolean;
  customerName?: string;
  shippingAddress?: {
    streetAddress: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  } | null;
  salesOrderId?: string | null;
  lineItems?: Partial<LoadLineItemRecord>[];
  materialItemIds: string[];
  lineItemCount: number;
};

export interface LoadRecord {
  id: string;
  version?: number;
  loadNumber: string;
  status: LoadStatus;
  currencyCode?: CurrencyCode;
  reviewed: boolean;
  description?: string | null | undefined;
  expectedShipDate?: CalendarDate;
  shippedDate?: CalendarDate | null;
  loadReceivedDate?: CalendarDate | null;
  customerAccountId?: string | null | undefined;
  customerAccountAddressId?: string | null | undefined;
  salesOrderId?: string | null | undefined;
  invoiceId?: string | null | undefined;
  salesOrderNumber?: string | null | undefined;
  bookingId?: string | null | undefined;
  bookingNumber?: string | null | undefined;
  bookingStatus?: BookingStatus;
  facilityId?: string | null | undefined;
  outboundTollingAgreementId?: string | null | undefined;
  receivingFacilityId?: string | null | undefined;
  customerName?: string | null | undefined;
  recyclerInternalNotes?: string | null | undefined;
  recyclerExternalNotes?: string | null | undefined;
  customerInternalNotes?: string | null | undefined;
  customerExternalNotes?: string | null | undefined;
  salesOrder?: {
    id: string;
    documentNumber: string;
    customerName: string | null;
    counterPartyPurchaseOrderNumber?: string | null;
    paymentTerm?: PaymentTerm;
    shippingAddress?: {
      streetAddress: string | null;
      city: string | null;
      state: string | null;
      postalCode: string | null;
    } | null;
  } | null;
  carrierAccountId?: string | null | undefined;
  freightTerms?: string | null | undefined;
  shippingCarrierName?: string | null | undefined;
  scac?: string | null | undefined;
  freightForwarder?: string | null | undefined;
  truckFreight?: string | null | undefined;
  deliveryNumber?: string | null | undefined;
  releaseNumber?: string | null | undefined;
  driverName?: string | null | undefined;
  truckNumber?: string | null | undefined;
  trailerNumber?: string | null | undefined;
  containerTypeId?: string | null | undefined;
  containerType?:
    | {
        id: string;
        name: string;
      }
    | null
    | undefined;
  containerNumber?: string | null | undefined;
  containerId?: string | null | undefined;
  container?: {
    id: string;
    containerNumber: string | null;
  } | null;
  sealNumber?: string | null | undefined;
  bolNumber?: string | null | undefined;
  heavyWeight?: Weight | null;
  lightWeight?: Weight | null;
  internalNotes?: string | null | undefined;
  externalNotes?: string | null | undefined;
  accountId?: string;
  files?: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    status: string;
    downloadUrl?: string;
    entityType?: string;
    entityId?: string;
    metadata?: FileMetadata;
    createdAt: ZonedDateTime;
    updatedAt: ZonedDateTime;
  }>;
  timestamps: {
    createdAt: ZonedDateTime;
    updatedAt?: ZonedDateTime;
  };
  weightDisplayUnit?: WeightDisplayUnit;
  customer?: string;
  lineItemCount?: number;
  netQuantity?: number;
  netRequestedQuantity?: number;
  netOwedQuantity?: number;
  createdBy?: string;
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  shippedNetWeight?: number;
  shippedTotal?: number;
  receivingTicketNumber?: string | null;
  receivedTicketIssueDate?: CalendarDate | null;
  millReceivedWeightPostDate?: CalendarDate | null;
  lineItems: LoadLineItemRecord[];
  reconciliation?: Pick<
    LoadReconciliation,
    'id' | 'receivedLineItems' | 'deductions' | 'freightCharges'
  > | null;
  subtotal?: Money | null;
  deductionsTotal?: Money | null;
  grandTotal?: Money | null;
  billId?: string | null;
  deductions?: LoadDeductionRecord[];
}

export type LoadFormLineItem = {
  [K in keyof Omit<LoadLineItem, 'loadId' | '__typename' | 'item' | 'id'>]?:
    | LoadLineItem[K]
    | null
    | undefined;
} & {
  id: string;
  item?: {
    id: string;
    name: string;
    skuNumber?: string | null;
  } | null;
  includeInSalesOrder?: boolean;
  pendingSalesOrderMaterialSelection?: boolean;
  aggregatedTags?: {
    id: string;
    netQuantity: number;
    lineItemId?: string;
    weights?: Partial<Record<enumLoadWeightType, Weight>> | null;
  }[];
  untaggedLineItem?: {
    id: string;
    netQuantity: number;
  };
  catalyticConverterSize?: string;
  photo?: LineItemPhoto;
};

export type LoadFormState = {
  [K in keyof Omit<LoadRecord, 'timestamps' | 'lineItems'>]?: LoadRecord[K];
} & {
  salesOrderStatus?: SalesOrderStatus;
  lineItems: LoadFormLineItem[];
};

export type InventoryItemRecord = {
  id: string;
  name: string;
  mnemonic?: string;
  colorHue?: number | null;
  effectivePricingUnit?: string;
  categoryId?: string;
  category?: {
    name: string;
  } | null;
  itemGroupId?: string;
  itemGroup?: {
    id: string;
    name: string;
    number?: string;
    colorHue?: number | null;
  } | null;
  metadata?: unknown;
};

export type CatalyticConverterSize =
  | 'whole'
  | 'threeQuarters'
  | 'half'
  | 'oneQuarter';

export type CatalyticConverterSizeOption = {
  key: CatalyticConverterSize;
  label: string;
};

export type CatalyticItemMetadata = {
  attributeValues?: string;
};

export type CatalyticItemGroup = {
  groupKey: string;
  groupId: string;
  groupName: string;
  itemName: string;
  representativeItem: InventoryItemRecord;
  availableSizes: CatalyticConverterSize[];
  itemsBySize: Map<CatalyticConverterSize, InventoryItemRecord>;
};

export type GroupedCatalyticItems = {
  groups: CatalyticItemGroup[];
  groupsByKey: Map<string, CatalyticItemGroup>;
  representativeItems: InventoryItemRecord[];
};
